// Currency formatting utility functions

/**
 * Format a number as currency with the selected currency symbol
 * @param {number} amount - The amount to format
 * @param {Object} currency - The currency object with code and name
 * @param {string} locale - The locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */


const CURRENCY_SYMBOL_OVERRIDE = {
  USD: '$',
  CAD: 'CA$',
  EUR: '€',
};

const LOCALE_BY_CURRENCY = {
  USD: 'en-US',
  CAD: 'en-CA',
  EUR: 'en-IE',
};
 
export const formatCurrency = (amount, currency, locale = 'en-US') => {
  if (!amount || !currency) return '';
  
  try {
    // Create a number formatter with the currency code
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return formatter.format(amount);
  } catch (error) {
    // Fallback formatting if the currency code is not supported
    const symbol = getCurrencySymbol(currency.code);
    return `${symbol}${amount.toFixed(2)}`;
  }
};


// export const formatCurrency = (amount, currencyOrCode) => {
//   if (amount == null || !currencyOrCode) return "";

//   const code =
//     typeof currencyOrCode === "string"
//       ? currencyOrCode.trim().toUpperCase()
//       : currencyOrCode.code?.trim().toUpperCase();

//   if (!code) return "";

//   const locale =
//     code === "USD"
//       ? "en-US"
//       : code === "CAD"
//         ? "en-CA"
//         : code === "EUR"
//           ? "en-IE"
//           : "en-US"; // Default to en-US for international 3-digit comma formatting

//   const formatter = new Intl.NumberFormat(locale, {
//     style: "currency",
//     currency: code,
//     currencyDisplay: "narrowSymbol",
//     /* Changed both to 0 to force whole numbers */
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   });

//   /* Added Math.round to ensure 6768.87 becomes 6769 */
//   return formatter
//     .formatToParts(Math.round(Number(amount)))
//     .map((part) => {
//       if (part.type !== "currency") return part.value;
//       if (code === "USD") return "$";
//       if (code === "CAD") return "CA$";
//       if (code === "EUR") return "€";
//       return part.value;
//     })
//     .join("");
// };


/**
 * Format a number as currency with custom symbol placement
 * @param {number} amount - The amount to format
 * @param {Object} currency - The currency object with code and name
 * @param {boolean} symbolBefore - Whether to place symbol before the amount (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrencyCustom = (amount, currency, symbolBefore = true) => {
  if (!amount || !currency) return '';
  
  const formattedAmount = amount.toFixed(2);
  const symbol = getCurrencySymbol(currency.code);
  
  if (symbolBefore) {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount}${symbol}`;
  }
};

/**
 * Convert amount using the selectedCurrencyConversion rate from API response
 * @param {number} amount - The original amount from API
 * @param {number} conversionRate - The conversion rate from selectedCurrencyConversion field
 * @returns {number} Converted amount
 */
export const convertAmountWithRate = (amount, conversionRate) => {
  if (!amount || !conversionRate || conversionRate === 0) return amount;
  return amount * conversionRate;
};

/**
 * Convert amount from one currency to another using exchange rates
 * @param {number} amount - The amount to convert
 * @param {Object} fromCurrency - The source currency
 * @param {Object} toCurrency - The target currency
 * @param {Object} exchangeRates - Object containing exchange rates
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates) => {
  if (!amount || !fromCurrency || !toCurrency || !exchangeRates) return amount;
  
  // If currencies are the same, return the original amount
  if (fromCurrency.code === toCurrency.code) return amount;
  
  // Get exchange rates (assuming rates are relative to USD)
  const fromRate = exchangeRates[fromCurrency.code] || 1;
  const toRate = exchangeRates[toCurrency.code] || 1;
  
  // Convert to USD first, then to target currency
  const usdAmount = amount / fromRate;
  return usdAmount * toRate;
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - The currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const currencyMap = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'INR': '₹',
    'BRL': 'R$',
    'RUB': '₽',
    'KRW': '₩',
    'MXN': '$',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NOK': 'kr',
    'SEK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'TRY': '₺',
    'ZAR': 'R',
    'THB': '฿',
    'MYR': 'RM',
    'IDR': 'Rp',
    'PHP': '₱',
    'VND': '₫',
    'EGP': 'E£',
    'NGN': '₦',
    'KES': 'KSh',
    'GHS': 'GH₵',
    'UGX': 'USh',
    'TZS': 'TSh',
    'MAD': 'MAD',
    'AED': 'د.إ',
    'SAR': 'ر.س',
    'QAR': 'ر.ق',
    'KWD': 'د.ك',
    'BHD': '.د.ب',
    'OMR': 'ر.ع.',
    'JOD': 'د.ا',
    'LBP': 'ل.ل',
    'ILS': '₪',
    'CLP': '$',
    'COP': '$',
    'PEN': 'S/',
    'ARS': '$',
    'UYU': '$',
    'PYG': '₲',
    'BOB': 'Bs',
    'CRC': '₡',
    'GTQ': 'Q',
    'HNL': 'L',
    'NIO': 'C$',
    'PAB': 'B/.',
    'DOP': 'RD$',
    'TTD': 'TT$',
    'JMD': 'J$',
    'BBD': 'Bds$',
    'XCD': 'EC$',
    'BZD': 'BZ$',
    'GYD': 'G$',
    'SRD': '$',
    'FJD': 'FJ$',
    'NZD': 'NZ$',
    'PGK': 'K',
    'SBD': 'SI$',
    'VUV': 'VT',
    'WST': 'WS$',
    'TOP': 'T$',
    'KID': '$',
    'TVD': '$',
    'XPF': 'CFP',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RON': 'lei',
    'BGN': 'лв',
    'HRK': 'kn',
    'RSD': 'дин',
    'ALL': 'L',
    'MKD': 'ден',
    'MDL': 'L',
    'UAH': '₴',
    'BYN': 'Br',
    'GEL': '₾',
    'AMD': '֏',
    'AZN': '₼',
    'KZT': '₸',
    'KGS': 'с',
    'TJS': 'ЅМ',
    'TMT': 'T',
    'UZS': 'so\'m',
    'MNT': '₮',
    'LAK': '₭',
    'KHR': '៛',
    'MMK': 'K',
    'BDT': '৳',
    'LKR': 'Rs',
    'NPR': 'रू',
    'PKR': '₨',
    'AFN': '؋',
    'IRR': '﷼',
    'IQD': 'ع.د',
    'SYP': 'ل.س',
    'YER': '﷼',
    'BIF': 'FBu',
    'CDF': 'FC',
    'DJF': 'Fdj',
    'ETB': 'Br',
    'GMD': 'D',
    'GNF': 'FG',
    'LRD': 'L$',
    'MWK': 'MK',
    'MUR': '₨',
    'MZN': 'MT',
    'NAD': 'N$',
    'RWF': 'FRw',
    'SLL': 'Le',
    'SOS': 'S',
    'SSP': 'SSP',
    'STN': 'Db',
    'SZL': 'L',
    'TND': 'د.ت',
    'XOF': 'CFA',
    'XAF': 'FCFA',
    'CVE': '$',
    'KMF': 'CF',
    'MGA': 'Ar',
    'MRO': 'UM',
    'ANG': 'ƒ',
    'AWG': 'ƒ',
    'BMD': '$',
    'KYD': '$',
    'FKP': '£',
    'GIP': '£',
    'IMP': '£',
    'JEP': '£',
    'SHP': '£'
  };
  
  return currencyMap[currencyCode] || currencyCode;
};

/**
 * Validate if a currency code is supported
 * @param {string} currencyCode - The currency code to validate
 * @returns {boolean} True if the currency code is supported
 */
export const isValidCurrencyCode = (currencyCode) => {
  const supportedCurrencies = [
    'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'BRL',
    'RUB', 'KRW', 'MXN', 'SGD', 'HKD', 'NOK', 'SEK', 'DKK', 'PLN', 'TRY',
    'ZAR', 'THB', 'MYR', 'IDR', 'PHP', 'VND', 'EGP', 'NGN', 'KES', 'GHS',
    'UGX', 'TZS', 'MAD', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD',
    'LBP', 'ILS', 'CLP', 'COP', 'PEN', 'ARS', 'UYU', 'PYG', 'BOB', 'CRC',
    'GTQ', 'HNL', 'NIO', 'PAB', 'DOP', 'TTD', 'JMD', 'BBD', 'XCD', 'BZD',
    'GYD', 'SRD', 'FJD', 'NZD', 'PGK', 'SBD', 'VUV', 'WST', 'TOP', 'KID',
    'TVD', 'XPF', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RSD', 'ALL', 'MKD',
    'MDL', 'UAH', 'BYN', 'GEL', 'AMD', 'AZN', 'KZT', 'KGS', 'TJS', 'TMT',
    'UZS', 'MNT', 'LAK', 'KHR', 'MMK', 'BDT', 'LKR', 'NPR', 'PKR', 'AFN',
    'IRR', 'IQD', 'SYP', 'YER', 'BIF', 'CDF', 'DJF', 'ETB', 'GMD', 'GNF',
    'LRD', 'MWK', 'MUR', 'MZN', 'NAD', 'RWF', 'SLL', 'SOS', 'SSP', 'STN',
    'SZL', 'TND', 'XOF', 'XAF', 'CVE', 'KMF', 'MGA', 'MRO', 'ANG', 'AWG',
    'BMD', 'KYD', 'FKP', 'GIP', 'IMP', 'JEP', 'SHP'
  ];
  
  return supportedCurrencies.includes(currencyCode);
};
